import { Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import {
  PeopleSearchStatus,
  usePeopleSearch,
} from "@mirage/search/Header/usePeopleSearch";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { useActivityFeed } from "@mirage/service-feed/hooks/useActivityFeed";
import {
  useOrgChart,
  usePersonFromOrgChartByAccountId,
} from "@mirage/service-people/hooks";
import {
  getDirectReports,
  getPeers,
  getReportingLine,
} from "@mirage/service-people/service/utils";
import type { PersonOrgChart } from "@mirage/service-people/types";
import { DashSpinner } from "@mirage/shared/dash-spinner";
import i18n from "@mirage/translations";
import React from "react";
import styles from "./FollowingPage.module.css";
import { GroupedProfilesCard } from "./GroupedProfilesCard";
import { ProfileCard } from "./ProfileCard";

const maxVisibleProfiles = 4;
export const FollowingPage: React.FC = () => {
  const { peopleSuggestions, peopleSuggestionsStatus } = usePeopleSearch(8);
  const account = useDropboxAccount();
  const currentPerson = usePersonFromOrgChartByAccountId(account?.account_id);
  const orgChart = useOrgChart();
  const activityActors = useGetActivityActors(currentPerson);
  const handleFollowAllClick = () => {};

  const peers =
    currentPerson && orgChart ? getPeers(currentPerson, orgChart) : [];
  const reportingLine =
    currentPerson && orgChart ? getReportingLine(currentPerson, orgChart) : [];
  const directReports =
    currentPerson && orgChart ? getDirectReports(currentPerson, orgChart) : [];
  const chiefs =
    currentPerson && orgChart ? getChiefs(currentPerson, orgChart) : [];
  reportingLine.shift();
  return (
    <Box className={styles.container}>
      {currentPerson && orgChart && (
        <Box className={styles.sectionContainer}>
          <Title size="large" className={styles.sectionTitle}>
            Follow to keep up with their work
          </Title>
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            columnGap="Micro Small"
            rowGap="Micro Small"
            className={styles.groupSuggestions}
          >
            <GroupedProfilesCard
              teamName={"Collaborators"}
              profiles={activityActors}
              primaryProfile={activityActors[0]}
              onFollowAllClick={handleFollowAllClick}
              maxVisibleProfiles={maxVisibleProfiles}
              groupName="People from my feed"
            />
            <GroupedProfilesCard
              teamName={currentPerson.teams[0]}
              profiles={peers}
              primaryProfile={peers[0]}
              onFollowAllClick={handleFollowAllClick}
              maxVisibleProfiles={maxVisibleProfiles}
              groupName="Peers"
            />
            <GroupedProfilesCard
              teamName={"My reporting line"}
              profiles={reportingLine}
              primaryProfile={reportingLine[0]}
              onFollowAllClick={handleFollowAllClick}
              maxVisibleProfiles={maxVisibleProfiles}
              groupName="Reporting Line"
            />
            <GroupedProfilesCard
              teamName={"Direct reports"}
              profiles={directReports}
              primaryProfile={directReports[0]}
              onFollowAllClick={handleFollowAllClick}
              maxVisibleProfiles={maxVisibleProfiles}
              groupName="Direct reports"
            />
            <GroupedProfilesCard
              teamName={"Leaders"}
              profiles={chiefs}
              primaryProfile={chiefs[0]}
              onFollowAllClick={handleFollowAllClick}
              maxVisibleProfiles={maxVisibleProfiles}
              groupName="Chiefs of ..."
            />
          </Box>
        </Box>
      )}
      <Box className={styles.sectionContainer}>
        <Title size="large" className={styles.sectionTitle}>
          {i18n.t("following_page_title")}
        </Title>

        <Box
          display="flex"
          flexDirection="row"
          flexWrap="wrap"
          columnGap="Micro Small"
          rowGap="Micro Small"
          className={styles.peopleSuggestions}
        >
          {peopleSuggestionsStatus === PeopleSearchStatus.INITIAL ? (
            <Box className={styles.loaderContainer}>
              <DashSpinner size={32} />
            </Box>
          ) : peopleSuggestions.length > 0 ? (
            peopleSuggestions.map((person) => (
              <ProfileCard
                key={`profile-card__${person.accountId}`}
                user={{
                  name: person.displayName ?? "",
                  role: person.role ?? "",
                  profilePhotoUrl: person.profilePhotoUrl ?? "",
                }}
                isRecentCollaborator={false}
                onFollowClick={() => {}}
                isFollowing={false}
              />
            ))
          ) : (
            <Box className={styles.emptyState}>
              {i18n.t("following_page_empty_state")}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const getChiefs = (
  person: PersonOrgChart,
  allPeople: PersonOrgChart[],
  includeSelf: boolean = false,
): PersonOrgChart[] => {
  if (!person.managerUuid) {
    return [];
  }
  return allPeople.filter(
    (p) =>
      p.role.toLocaleLowerCase().startsWith("chief") &&
      (includeSelf || p.uuid !== person.uuid),
  );
};

const useGetActivityActors = (person: PersonOrgChart | null | undefined) => {
  const { activities } = useActivityFeed();
  const emails = new Set<string>();
  const result: PersonOrgChart[] = [];
  if (!person) {
    return result;
  }
  activities?.forEach((activity) => {
    (activity.activities || [])
      .filter((a) => a.actor?.email !== person.email)
      .forEach((a) => {
        if (a.actor && a.actor.email && !emails.has(a.actor.email)) {
          emails.add(a.actor.email);
          const id = a.actor.user_id?.toString() || "";
          result.push({
            uuid: id,
            accountId: id,
            managerUuid: id,
            email: a.actor.email,
            displayName: a.actor.name || "",
            displayHandle: a.actor.name || "",
            profilePhotoUrl: a.actor.avatar_img,
            directReportCount: 0,
            totalReportCount: 0,
            onLeave: false,
            isDeactivated: false,
            role: "",
            teams: [],
            birthday: null,
            tenure: null,
            type: "",
            rank: 1,
          });
        }
      });
  });
  return result;
};
